import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { X, Plus, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function FiltersPanel() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-filters"], queryFn: async () => (await api.get("/admin/filters")).data });
  const [form, setForm] = useState(null);
  const [word, setWord] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (data) setForm(data); }, [data]);
  if (!form) return <Loader2 className="w-6 h-6 animate-spin text-[#8012C6] mx-auto" />;

  const add = (e) => {
    e.preventDefault();
    const w = word.trim().toLowerCase();
    if (w && !form.banned_words.includes(w)) setForm({ ...form, banned_words: [...form.banned_words, w] });
    setWord("");
  };
  const remove = (w) => setForm({ ...form, banned_words: form.banned_words.filter((x) => x !== w) });
  const save = async () => {
    setBusy(true);
    try {
      const { data: saved } = await api.put("/admin/filters", form);
      setForm(saved);
      qc.setQueryData(["admin-filters"], saved);
      qc.invalidateQueries({ queryKey: ["admin-log"] });
      toast.success("Filters saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="filters-panel" className="bg-card border border-border rounded-2xl p-5 space-y-6">
      <div>
        <h3 className="font-heading font-bold">Banned words & phrases</h3>
        <p className="text-xs text-muted-foreground mt-1">Posts, comments and messages containing these are blocked automatically and logged.</p>
        <form onSubmit={add} className="flex gap-2 mt-3">
          <Input data-testid="filter-word-input" value={word} onChange={(e) => setWord(e.target.value)} placeholder="Add a word or phrase" className="rounded-xl bg-input border-border" />
          <Button data-testid="filter-word-add" type="submit" variant="outline" className="rounded-xl"><Plus className="w-4 h-4" /></Button>
        </form>
        <div className="flex flex-wrap gap-2 mt-3">
          {form.banned_words.map((w) => (
            <span key={w} data-testid={`filter-word-${w}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-secondary text-xs">
              {w}<button data-testid={`filter-word-remove-${w}`} onClick={() => remove(w)} className="hover:text-red-400"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="text-sm">
          <span className="font-semibold">Link wait for unverified accounts (hours)</span>
          <Input data-testid="filter-link-age" type="number" min={0} max={720} value={form.link_min_age_hours} onChange={(e) => setForm({ ...form, link_min_age_hours: Number(e.target.value) })} className="mt-1 rounded-xl bg-input border-border" />
        </label>
        <label className="text-sm">
          <span className="font-semibold">Max links per post / message</span>
          <Input data-testid="filter-max-links" type="number" min={0} max={20} value={form.max_links} onChange={(e) => setForm({ ...form, max_links: Number(e.target.value) })} className="mt-1 rounded-xl bg-input border-border" />
        </label>
      </div>
      <p className="text-xs text-muted-foreground">Rate limits: new unverified accounts (under 3 days) can make 5 posts, 15 comments and 30 messages per hour. Everyone else: 30 / 60 / 200.</p>
      <Button data-testid="filters-save-btn" onClick={save} disabled={busy} className="rounded-xl bg-[#6C0BA9] hover:bg-[#8012C6] text-white">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save filters"}
      </Button>
    </div>
  );
}
